import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, type FieldPath } from 'react-hook-form';
import { Button } from '@/components/Button';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/useToast';
import type { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { DATA_UPDATE_GROUPS, type DataUpdateGroupId, type DpAttachment } from '../../../../../shared/dpRequests';
import { DataUpdateGroupPicker } from '../DataUpdateGroupPicker';
import { DataUpdateSections } from '../DataUpdateSections';
import { AttachmentPicker } from '../AttachmentPicker';
import { MAX_ATTACHMENTS } from '../../dpRequests.definitions';
import { withUploadedAttachments, type PendingFile } from '../../services/dpAttachmentUpload';
import { useResubmitDataUpdate, useSubmitDataUpdate } from '../../hooks/data/useDpRequestMutations';
import { useEditableProfileQuery } from '../../hooks/data/useDpRequestQueries';
import { buildDataUpdateSchema } from '../../models/dataUpdate.schemas';
import { pickChangedValues } from '../../utils/buildDataUpdatePayload';
import { DATA_UPDATE_INTRO } from '../../dataUpdate.definitions';
import { DP_REQUESTS_PATH } from '../../routes';
import type { DataUpdateFormProps } from './dataUpdateForm.types';

export function DataUpdateForm({ profile, mode, initialGroups, initialAttachments, requestId, correctionNote }: DataUpdateFormProps) {
    const navigate = useNavigate();
    const toast = useToast();
    const { user } = useAuth();
    const [groups, setGroups] = useState<DataUpdateGroupId[]>(initialGroups);
    const [attachments, setAttachments] = useState<DpAttachment[]>(initialAttachments);
    const [files, setFiles] = useState<PendingFile[]>([]);
    const [uploading, setUploading] = useState(false);
    const initialValues = useRef<Record<string, unknown>>(profile.values);
    const form = useForm<CollaboratorFormData>({ defaultValues: profile.values as unknown as CollaboratorFormData });
    const submitNew = useSubmitDataUpdate();
    const resubmit = useResubmitDataUpdate();
    const isSubmitting = submitNew.isPending || resubmit.isPending || uploading;

    const educationRequested = groups.includes('education');
    const educationQuery = useEditableProfileQuery(educationRequested);

    useEffect(() => {
        const educations = educationQuery.data?.values.educations;
        if (!educations) return;
        initialValues.current = { ...initialValues.current, educations };
        form.setValue('educations' as never, educations as never, { shouldDirty: false });
    }, [educationQuery.data, form]);

    function toggleGroup(id: DataUpdateGroupId) {
        setGroups(current => (current.includes(id) ? current.filter(item => item !== id) : [...current, id]));
    }

    function addAttachment(attachment: DpAttachment) {
        setAttachments(current => [...current, attachment]);
    }

    async function handleSubmit() {
        if (groups.length === 0) {
            toast.error('Escolha pelo menos um item', 'Marque o que quer atualizar.');
            return;
        }
        if (educationRequested && !educationQuery.data) {
            toast.error('Aguarde', 'Carregando a formação atual.');
            return;
        }

        const parsed = buildDataUpdateSchema(groups).safeParse(form.getValues());
        if (!parsed.success) {
            parsed.error.issues.forEach(issue => {
                form.setError(issue.path.join('.') as FieldPath<CollaboratorFormData>, { type: 'manual', message: issue.message });
            });
            toast.error('Revise os campos destacados');
            return;
        }

        const values = pickChangedValues(parsed.data as Record<string, unknown>, groups, initialValues.current);
        if (Object.keys(values).length === 0) {
            toast.error('Nenhuma alteração', 'Mude pelo menos um campo antes de enviar.');
            return;
        }

        if (!user?.uid) return;
        setUploading(true);
        try {
            await withUploadedAttachments(files, user.uid, async uploaded => {
                const payload = { groups, values, attachments: uploaded };
                if (mode === 'correction' && requestId) {
                    await resubmit.mutateAsync({ requestId, payload });
                } else {
                    await submitNew.mutateAsync(payload);
                }
            }, attachments);
            navigate(DP_REQUESTS_PATH);
        } catch {
            return;
        } finally {
            setUploading(false);
        }
    }

    return (
        <div className="space-y-6">
            {mode === 'correction' && correctionNote && (
                <div className="rounded-2xl border border-orange-200 bg-orange-50 px-5 py-4 text-sm text-orange-800">
                    <p className="font-semibold">O DP pediu um ajuste</p>
                    <p className="mt-1 whitespace-pre-wrap">{correctionNote}</p>
                </div>
            )}

            <div>
                <p className="text-sm text-raro-gray-600">{DATA_UPDATE_INTRO}</p>
            </div>

            <DataUpdateGroupPicker
                groups={[...DATA_UPDATE_GROUPS]}
                values={profile.values}
                selected={groups}
                onToggle={toggleGroup}
                disabled={isSubmitting}
            />

            {educationRequested && educationQuery.isPending && (
                <p className="text-sm text-raro-gray-500" role="status">Carregando sua formação…</p>
            )}

            {groups.length > 0 && (
                <DataUpdateSections
                    groups={groups}
                    form={form}
                    collaboratorId={profile.collaboratorId}
                    isSubmitting={isSubmitting}
                    onProofUploaded={addAttachment}
                />
            )}

            {groups.length > 0 && (
                <div className="rounded-2xl border border-raro-gray-200 bg-white p-5 shadow-sm">
                    <AttachmentPicker
                        files={files}
                        onAdd={file => setFiles(current => [...current, file])}
                        onRemove={index => setFiles(current => current.filter((_, position) => position !== index))}
                        label="Comprovantes"
                        hint="Opcional. PDF, JPG ou PNG. O arquivo só é enviado quando você enviar o pedido."
                        disabled={isSubmitting}
                        maxFiles={MAX_ATTACHMENTS - attachments.length}
                    />
                </div>
            )}

            <div className="sticky bottom-0 -mx-4 flex flex-col-reverse gap-3 border-t border-raro-gray-200 bg-white/95 px-4 py-4 backdrop-blur sm:mx-0 sm:flex-row sm:items-center sm:justify-end sm:rounded-2xl sm:border">
                <Button variant="ghost" onClick={() => navigate(DP_REQUESTS_PATH)} disabled={isSubmitting}>
                    Cancelar
                </Button>
                <Button onClick={() => void handleSubmit()} loading={isSubmitting} disabled={groups.length === 0}>
                    {mode === 'correction' ? 'Reenviar ao DP' : 'Enviar ao DP'}
                </Button>
            </div>
        </div>
    );
}
